import { createReducer, createActions } from 'reduxsauce'
import {
  failureFeedback,
  initialFeedback,
  requestFeedback,
  successFeedback
} from './utils'

const INITIAL_STATE = {
  tentativeParents: [],
  feedback: initialFeedback(),
  searchResults: [],
  searchFeedback: initialFeedback(),
  showParentErrorModal: false,
  showParentConfirmModal: false,
  selectedParentForConfirmation: null,
  shouldShowParentSelectorModal: false
}

const { Types, Creators } = createActions(
  {
    all: ['params'],
    allSuccess: ['tentativeParents'],
    allFailure: ['errors'],
    remove: ['agentId'],
    removeSuccess: ['agentId'],
    removeFailure: ['errors'],
    choose: ['agentId'],
    chooseSuccess: ['agentId'],
    chooseFailure: ['errors', 'agentId'],
    search: ['searchTerm'],
    searchSuccess: ['searchResults'],
    searchFailure: ['errors'],
    clearSearch: [],
    clear: [],
    closeParentErrorModal: [],
    showParentConfirmModal: ['parent'],
    hideParentConfirmModal: [],
    confirmParentSelection: [],
    checkParentSelectorPermissions: [],
    closeParentSelectorModal: [],
    setShouldShowModal: ['shouldShow']
  },
  { prefix: 'TENTATIVE_PARENTS_' }
)

const all = (state, { params }) => ({
  ...state,
  feedback: requestFeedback()
})

const allSuccess = (state, { tentativeParents }) => ({
  ...state,
  tentativeParents,
  feedback: successFeedback()
})

const allFailure = (state, { errors }) => ({
  ...state,
  feedback: failureFeedback(errors)
})

const remove = (state, { agentId }) => ({
  ...state,
  feedback: requestFeedback()
})

const removeSuccess = (state, { agentId }) => ({
  ...state,
  tentativeParents: state.tentativeParents.filter((parent) => (
    parent.parent && parent.parent.id !== agentId
  )),
  feedback: successFeedback()
})

const removeFailure = (state, { errors }) => ({
  ...state,
  feedback: failureFeedback(errors)
})

const choose = (state, { agentId }) => ({
  ...state,
  feedback: requestFeedback()
})

const chooseSuccess = (state, { agentId }) => ({
  ...state,
  feedback: successFeedback()
})

const chooseFailure = (state, { errors, agentId }) => {
  // Check if the error contains parent validation error
  const hasParentError = !!(errors && errors.parent)

  // If there's a parent error, remove the parent from tentativeParents
  const updatedTentativeParents = hasParentError && agentId
    ? state.tentativeParents.filter((parent) => (
      parent.parent && parent.parent.id !== agentId
    ))
    : state.tentativeParents

  return {
    ...state,
    tentativeParents: updatedTentativeParents,
    showParentErrorModal: hasParentError,
    // Only set error feedback if it's not a parent error (which is handled by modal)
    feedback: hasParentError ? successFeedback() : failureFeedback(errors)
  }
}

const clear = () => INITIAL_STATE

const search = (state, { searchTerm }) => ({
  ...state,
  searchFeedback: requestFeedback()
})

const searchSuccess = (state, { searchResults }) => ({
  ...state,
  searchResults,
  searchFeedback: successFeedback()
})

const searchFailure = (state, { errors }) => ({
  ...state,
  searchFeedback: failureFeedback(errors)
})

const clearSearch = (state) => ({
  ...state,
  searchResults: [],
  searchFeedback: initialFeedback()
})

const closeParentErrorModal = (state) => ({
  ...state,
  showParentErrorModal: false
})

const showParentConfirmModal = (state, { parent }) => ({
  ...state,
  showParentConfirmModal: true,
  selectedParentForConfirmation: parent
})

const hideParentConfirmModal = (state) => ({
  ...state,
  showParentConfirmModal: false,
  selectedParentForConfirmation: null
})

const confirmParentSelection = (state) => ({
  ...state,
  showParentConfirmModal: false,
  selectedParentForConfirmation: null,
  feedback: requestFeedback()
})

const checkParentSelectorPermissions = (state) => ({
  ...state,
  feedback: requestFeedback()
})

const closeParentSelectorModal = (state) => ({
  ...state,
  shouldShowParentSelectorModal: false
})

const setShouldShowModal = (state, { shouldShow }) => ({
  ...state,
  shouldShowParentSelectorModal: shouldShow
})

export const TentativeParentsActionTypes = Types

const HANDLERS = {
  [Types.ALL]: all,
  [Types.ALL_SUCCESS]: allSuccess,
  [Types.ALL_FAILURE]: allFailure,
  [Types.REMOVE]: remove,
  [Types.REMOVE_SUCCESS]: removeSuccess,
  [Types.REMOVE_FAILURE]: removeFailure,
  [Types.CHOOSE]: choose,
  [Types.CHOOSE_SUCCESS]: chooseSuccess,
  [Types.CHOOSE_FAILURE]: chooseFailure,
  [Types.SEARCH]: search,
  [Types.SEARCH_SUCCESS]: searchSuccess,
  [Types.SEARCH_FAILURE]: searchFailure,
  [Types.CLEAR_SEARCH]: clearSearch,
  [Types.CLEAR]: clear,
  [Types.CLOSE_PARENT_ERROR_MODAL]: closeParentErrorModal,
  [Types.SHOW_PARENT_CONFIRM_MODAL]: showParentConfirmModal,
  [Types.HIDE_PARENT_CONFIRM_MODAL]: hideParentConfirmModal,
  [Types.CONFIRM_PARENT_SELECTION]: confirmParentSelection,
  [Types.CHECK_PARENT_SELECTOR_PERMISSIONS]: checkParentSelectorPermissions,
  [Types.CLOSE_PARENT_SELECTOR_MODAL]: closeParentSelectorModal,
  [Types.SET_SHOULD_SHOW_MODAL]: setShouldShowModal
}

export const TentativeParentsActionCreators = Creators

export default createReducer(INITIAL_STATE, HANDLERS)
